// Presentation helpers (DESIGN_SPEC §5 typography, §12.16 formatting).
// Conventions: durations are milliseconds; instants are epoch ms, Date or ISO strings;
// a number and its unit are joined by NBSP ("955 KiB"); negative times use the real
// minus "−"; every clock and date is UTC (the report labels them so).
//
//   count(1204) "1,204" · bytes(977920) "955 KiB" · bytesExact(977920) "977,920 B"
//   hash8(sha) "a3f1c9e8…9a28b7c0"
//   rel(2930) "+00:02.930" · rel(-7700) "−00:07.700" · rel(3761000) "+1:02:41" · rel(26100, 1) "+00:26.1"
//   clock(185000) "3:05" · clock(185700, 1) "3:05.7" · clockOf(368000, 120000) "6:08 / 2:00"
//   dur(140) "0.14 s" · dur(25900) "25.9 s" · dur(552000) "9 min 12 s" · dur(5700000) "1 h 35 min"
//   utc(t) "06:51:31" · utc(t,"time-ms") "06:51:31.860" · utc(t,"hm") "08:25" · utc(t,"date") "Oct 12"
//   utc(t,"date-time") "Oct 11 23:26" · utc(t,"day") "Mon 12 Oct" · utc(t,"day-time") "Sat 10 Oct 08:56"
//   utc(t,"auto",now) today → "08:25", else "Oct 11 23:26" · utc(t,"iso") full ISO for <time datetime>
//   ago(t, now) "2 s ago" · "1 h 35 min ago" · agoFine(t, now) "0.4 s ago" (sub-10 s precision)
//   plural(2,"event") "events" · nOf(1204,"event") "1,204 events" · ordinal(1) "1st"
//   numWord(14) "fourteen" · capitalize("fourteen") "Fourteen" · times(1204) "×1.2k" · compact(1204) "1.2k"
//   winBase(p) "winsvc.exe" · winDir(p) "C:\…\WinSvc" · envPath(p) "%APPDATA%\WinSvc\winsvc.exe"

export const NBSP = "\u00a0";
export const MINUS = "\u2212";

const nf = new Intl.NumberFormat("en-US");

export type TimeInput = number | string | Date;

/** Epoch ms from a Date, ISO string or epoch ms (NaN when unparseable). */
export function toMs(t: TimeInput): number {
  if (typeof t === "number") return t;
  if (t instanceof Date) return t.getTime();
  return Date.parse(t);
}

/** Thousands-separated integer: 1,204. */
export function count(n: number): string {
  return Number.isFinite(n) ? nf.format(Math.round(n)) : "—";
}

/** Binary units, three significant figures, trailing zeros dropped: 312 B · 47.1 KiB · 955 KiB · 14 MiB · 2.04 GiB. */
export function bytes(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  if (Math.abs(n) < 1024) return `${count(n)}${NBSP}B`;
  const units = ["KiB", "MiB", "GiB", "TiB", "PiB"];
  let v = Math.abs(n) / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  let digits = v < 10 ? 2 : v < 100 ? 1 : 0;
  let s = v.toFixed(digits);
  if (Number(s) >= 1024 && i < units.length - 1) {
    // 1023.7 KiB rounds to "1024": carry into the next unit.
    v /= 1024;
    i++;
    digits = 2;
    s = v.toFixed(digits);
  }
  return `${n < 0 ? MINUS : ""}${stripZeros(s)}${NBSP}${units[i]}`;
}

/** Exact byte count: 977,920 B. */
export function bytesExact(n: number | null | undefined): string {
  return n == null || !Number.isFinite(n) ? "—" : `${count(n)}${NBSP}B`;
}

function stripZeros(s: string): string {
  return s.includes(".") ? s.replace(/\.?0+$/, "") : s;
}

/** First and last 8 characters of a hash: a3f1c9e8…9a28b7c0. */
export function hash8(h: string | null | undefined): string {
  if (!h) return "";
  return h.length > 17 ? `${h.slice(0, 8)}…${h.slice(-8)}` : h;
}

const pad = (n: number, w = 2) => String(n).padStart(w, "0");

/** T0-relative time: +00:02.930 · −00:07.700 · +1:02:41 past an hour. `digits` = fractional second digits (0–3). */
export function rel(ms: number, digits: 0 | 1 | 2 | 3 = 3): string {
  if (!Number.isFinite(ms)) return "—";
  const scale = 10 ** (3 - digits);
  const units = Math.round(Math.abs(ms) / scale); // in 10^-digits seconds
  const sign = ms < 0 && units > 0 ? MINUS : "+";
  const totalSec = Math.floor((units * scale) / 1000);
  if (totalSec >= 3600) {
    const h = Math.floor(totalSec / 3600);
    return `${sign}${h}:${pad(Math.floor((totalSec % 3600) / 60))}:${pad(totalSec % 60)}`;
  }
  const frac = digits ? `.${pad(units % 10 ** digits, digits)}` : "";
  return `${sign}${pad(Math.floor(totalSec / 60))}:${pad(totalSec % 60)}${frac}`;
}

/** Counting clock: 3:05 · 1:02:41 · clock(185700, 1) "3:05.7". Never negative; whole seconds floor. */
export function clock(ms: number, digits: 0 | 1 = 0): string {
  if (!Number.isFinite(ms)) return "—";
  const a = Math.max(0, ms);
  const totalSec = Math.floor(a / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const frac = digits ? `.${Math.floor((a % 1000) / 100)}` : "";
  return h ? `${h}:${pad(m)}:${pad(s)}${frac}` : `${m}:${pad(s)}${frac}`;
}

/** Elapsed against a budget: 6:08 / 2:00. */
export function clockOf(ms: number, totalMs: number): string {
  return `${clock(ms)} / ${clock(totalMs)}`;
}

/** Human duration: 4 ms · 0.14 s · 7.30 s · 25.9 s · 9 min 12 s · 1 h 35 min. */
export function dur(ms: number): string {
  if (!Number.isFinite(ms)) return "—";
  const a = Math.max(0, ms);
  if (a < 10) return `${Math.round(a)}${NBSP}ms`;
  if (a < 9995) return `${(a / 1000).toFixed(2)}${NBSP}s`;
  if (a < 59950) return `${(a / 1000).toFixed(1)}${NBSP}s`;
  const totalSec = Math.round(a / 1000);
  if (totalSec < 3600) {
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return s ? `${m}${NBSP}min ${s}${NBSP}s` : `${m}${NBSP}min`;
  }
  const totalMin = Math.round(totalSec / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m ? `${h}${NBSP}h ${m}${NBSP}min` : `${h}${NBSP}h`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

type UtcStyle = "time" | "time-ms" | "hm" | "date" | "date-time" | "day" | "day-time" | "auto" | "iso";

/** UTC wall-clock text. "auto" shows hh:mm for the same UTC day as `now`, else "Oct 11 23:26". */
export function utc(t: TimeInput, style: UtcStyle = "time", now: TimeInput = Date.now()): string {
  const ms = toMs(t);
  if (!Number.isFinite(ms)) return "—";
  const d = new Date(ms);
  const hm = `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  const hms = `${hm}:${pad(d.getUTCSeconds())}`;
  const date = `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}`;
  const day = `${WEEKDAYS[d.getUTCDay()]} ${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
  switch (style) {
    case "time":
      return hms;
    case "time-ms":
      return `${hms}.${pad(d.getUTCMilliseconds(), 3)}`;
    case "hm":
      return hm;
    case "date":
      return date;
    case "date-time":
      return `${date} ${hm}`;
    case "day":
      return day;
    case "day-time":
      return `${day} ${hm}`;
    case "iso":
      return d.toISOString();
    case "auto":
      return sameUtcDay(ms, toMs(now)) ? hm : `${date} ${hm}`;
  }
}

/** Same calendar day in UTC. */
export function sameUtcDay(a: TimeInput, b: TimeInput): boolean {
  const x = new Date(toMs(a));
  const y = new Date(toMs(b));
  return x.getUTCFullYear() === y.getUTCFullYear() && x.getUTCMonth() === y.getUTCMonth() && x.getUTCDate() === y.getUTCDate();
}

/** Coarse relative time: just now · 2 s ago · 4 min ago · 1 h 35 min ago · 2 d 3 h ago. */
export function ago(t: TimeInput, now: TimeInput = Date.now()): string {
  const ms = toMs(t);
  if (!Number.isFinite(ms)) return "—";
  const sec = Math.floor(Math.max(0, toMs(now) - ms) / 1000);
  if (sec < 1) return "just now";
  if (sec < 60) return `${sec}${NBSP}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}${NBSP}min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return min % 60 ? `${h}${NBSP}h ${min % 60}${NBSP}min ago` : `${h}${NBSP}h ago`;
  const days = Math.floor(h / 24);
  return h % 24 ? `${days}${NBSP}d ${h % 24}${NBSP}h ago` : `${days}${NBSP}d ago`;
}

/** Fine relative time for live lines: 0.4 s ago · 9.8 s ago, then as ago(). */
export function agoFine(t: TimeInput, now: TimeInput = Date.now()): string {
  const ms = toMs(t);
  if (!Number.isFinite(ms)) return "—";
  const d = Math.max(0, toMs(now) - ms);
  return d < 9950 ? `${(d / 1000).toFixed(1)}${NBSP}s ago` : ago(ms, now);
}

/** Whole seconds with unit, for idle/backoff counters: "12 s". */
export function secs(ms: number): string {
  return `${Math.max(0, Math.floor(ms / 1000))}${NBSP}s`;
}

/** The word for a count (no number): plural(1, "event") "event", plural(2, "event") "events". */
export function plural(n: number, one: string, many = `${one}s`): string {
  return n === 1 ? one : many;
}

/** Count and word: nOf(1204, "event") "1,204 events". */
export function nOf(n: number, one: string, many = `${one}s`): string {
  return `${count(n)} ${plural(n, one, many)}`;
}

/** 1st · 2nd · 3rd · 4th · 11th · 12th · 13th · 21st. */
export function ordinal(n: number): string {
  const v = Math.abs(Math.trunc(n));
  const teen = v % 100 >= 11 && v % 100 <= 13;
  const suffix = teen ? "th" : v % 10 === 1 ? "st" : v % 10 === 2 ? "nd" : v % 10 === 3 ? "rd" : "th";
  return `${Math.trunc(n)}${suffix}`;
}

/** A count in titles, ledes and prose: always digits, so a template never switches voice between 16 and 377. A
 *  sentence that reads better with a word for one or none says so itself ("one injection", "no host"). */
export function numWord(n: number): string {
  return count(n);
}

export function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}

/** Compact count: 999 · 1.2k · 12k · 1.2M. */
export function compact(n: number): string {
  const a = Math.abs(n);
  if (a < 1000) return count(n);
  if (a < 999_500) return `${stripZeros((n / 1e3).toFixed(a < 9_950 ? 1 : 0))}k`;
  return `${stripZeros((n / 1e6).toFixed(a < 9_950_000 ? 1 : 0))}M`;
}

/** Repeat count for folded rows: ×10 · ×1.2k. */
export function times(n: number): string {
  return `×${compact(n)}`;
}

// ---- Windows paths ------------------------------------------------------

/** Last path segment ("C:\\a\\b.exe" → "b.exe"); a trailing separator is ignored. */
export function winBase(p: string): string {
  const parts = p.split(/[\\/]+/).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : p;
}

/** Parent directory without the trailing separator ("C:\\a\\b.exe" → "C:\\a"). */
export function winDir(p: string): string {
  const trimmed = p.replace(/[\\/]+$/, "");
  const i = Math.max(trimmed.lastIndexOf("\\"), trimmed.lastIndexOf("/"));
  return i > 0 ? trimmed.slice(0, i) : "";
}

const ENV_PREFIXES: [RegExp, string][] = [
  [/^[a-z]:\\users\\public(?=\\|$)/i, "%PUBLIC%"],
  [/^[a-z]:\\users\\[^\\]+\\appdata\\roaming(?=\\|$)/i, "%APPDATA%"],
  [/^[a-z]:\\users\\[^\\]+\\appdata\\local\\temp(?=\\|$)/i, "%TEMP%"],
  [/^[a-z]:\\users\\[^\\]+\\appdata\\local(?=\\|$)/i, "%LOCALAPPDATA%"],
  [/^[a-z]:\\users\\[^\\]+(?=\\|$)/i, "%USERPROFILE%"],
  [/^[a-z]:\\programdata(?=\\|$)/i, "%PROGRAMDATA%"],
];

/** Collapse per-user prefixes to environment variables: %APPDATA%\WinSvc\winsvc.exe, %TEMP%\tmp8.tmp. */
export function envPath(p: string): string {
  for (const [re, v] of ENV_PREFIXES) if (re.test(p)) return p.replace(re, v);
  return p;
}
